"use client";

import { type CSSProperties, type ReactNode, useMemo } from "react";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
} from "../design-system/tooltip";
import { cn } from "../../lib";
import {
	OCTAGON_BADGE_BUTTON_ACTIVE_FACE_STYLE,
	type OctagonBadgeLayerOverrides,
	type OctagonBadgeSurface,
	resolveOctagonBadgeLayerStyle,
	type SkillBadgeVariant,
} from "./octagon-badge-layer-tokens";
import "./octagon-badge-surfaces.css";
import {
	buildOctagonClipPaths,
	OCTAGON_BUTTON_CLIP_GEOMETRY,
	OCTAGON_BUTTON_DEFAULT_SIZE_PX,
	resolveSkillBadgeOuterGlassSizePx,
	resolveSkillBadgeVisualFootprintPx,
	SKILL_BADGE_CLIP_GEOMETRY,
	SKILL_BADGE_OUTER_GLASS_CLIP_GEOMETRY,
} from "./octagon-geometry";

export type OctagonBadgeHoverVariant = "brightness" | "sidebar-highlight";

export type OctagonBadgeDropShadowPreset = "default" | "subtle";

/** Frosted outer rim for `surface="skill"` — mirrors {@link GlassBorder} tones. */
export type OctagonBadgeShellTone = "system" | "light";

/** Extra host padding per drop-shadow preset so filter output is not clipped. */
export const OCTAGON_BADGE_DROP_SHADOW_BLEED_PX: Record<
	OctagonBadgeDropShadowPreset,
	number
> = {
	default: 12,
	subtle: 6,
};

const OCTAGON_SHELL_CLIP_CLASS =
	"origin-center [clip-path:var(--sq-octagon-inner-clip)]";

const OCTAGON_STROKE_CLIP_CLASS =
	"origin-center [clip-path:var(--sq-octagon-stroke-clip)]";

const OCTAGON_FACE_CLIP_CLASS =
	"origin-center [clip-path:var(--sq-octagon-face-clip)]";

function resolveDropShadowPreset(
	dropShadow: boolean | OctagonBadgeDropShadowPreset,
): OctagonBadgeDropShadowPreset | false {
	if (dropShadow === false) {
		return false;
	}
	if (dropShadow === true) {
		return "default";
	}
	return dropShadow;
}

export interface OctagonBadgeProps {
	size?: number;
	surface?: OctagonBadgeSurface;
	layerOverrides?: OctagonBadgeLayerOverrides;
	className?: string;
	shadowHostClassName?: string;
	children?: ReactNode;
	/**
	 * Overlay clipped to the inner badge shape (inside the glass band).
	 * Renders above chrome, below icon; fully non-interactive.
	 */
	innerOverlay?: ReactNode;
	/** `true` → default preset; `false` → none; `"subtle"` for lighter elevation. */
	dropShadow?: boolean | OctagonBadgeDropShadowPreset;
	/** Button chrome — forwarded when `surface="button"`. */
	interactive?: boolean;
	active?: boolean;
	disabled?: boolean;
	hoverVariant?: OctagonBadgeHoverVariant;
	ariaHidden?: boolean;
	tooltip?: ReactNode;
	tooltipSide?: "top" | "right" | "bottom" | "left";
	tooltipSideOffset?: number;
	/**
	 * `visual` — layout box matches face size; shadow bleeds absolutely (stop headers).
	 * `shadow-host` — layout box includes shadow bleed inset.
	 */
	layoutFootprint?: "visual" | "shadow-host";
	/** Skill badge glass rim — `light` on dark panels; default `system`. */
	shellTone?: OctagonBadgeShellTone;
	/** Skill surface only — `earned` (default) or grey `placeholder` (unearned). */
	skillVariant?: SkillBadgeVariant;
	ariaLabel?: string;
}

/**
 * Layered clip-path octagon badge — shared chrome for interactive buttons and skill badges.
 */
export function OctagonBadge({
	size = OCTAGON_BUTTON_DEFAULT_SIZE_PX,
	surface = "custom",
	layerOverrides,
	className,
	shadowHostClassName,
	children,
	innerOverlay,
	dropShadow = true,
	interactive = false,
	active = false,
	disabled = false,
	hoverVariant = "brightness",
	ariaHidden = true,
	tooltip,
	tooltipSide = "top",
	tooltipSideOffset = 0,
	layoutFootprint = "shadow-host",
	shellTone = "system",
	skillVariant = "earned",
	ariaLabel,
}: OctagonBadgeProps) {
	/** Decorative (`interactive={false}`) must stay fully opaque — never couple to disabled opacity. */
	const isDisabled = disabled;
	const clipPaths = useMemo(
		() =>
			buildOctagonClipPaths(
				size,
				surface === "skill"
					? SKILL_BADGE_CLIP_GEOMETRY
					: OCTAGON_BUTTON_CLIP_GEOMETRY,
			),
		[size, surface],
	);
	const outerGlassClipPaths = useMemo(() => {
		if (surface !== "skill") {
			return null;
		}
		const outerGlassSize = resolveSkillBadgeOuterGlassSizePx(size);
		return buildOctagonClipPaths(
			outerGlassSize,
			SKILL_BADGE_OUTER_GLASS_CLIP_GEOMETRY,
		);
	}, [size, surface]);
	const dropShadowPreset = resolveDropShadowPreset(dropShadow);
	const shadowBleed = dropShadowPreset
		? OCTAGON_BADGE_DROP_SHADOW_BLEED_PX[dropShadowPreset]
		: 0;
	const hostSize = size + shadowBleed * 2;
	const footprintSize =
		layoutFootprint === "visual"
			? resolveSkillBadgeVisualFootprintPx(size)
			: hostSize;

	const tileStyle = useMemo(() => {
		const layerStyle = resolveOctagonBadgeLayerStyle(
			surface,
			layerOverrides,
		);
		const activeFaceStyle =
			surface === "button" && active
				? OCTAGON_BADGE_BUTTON_ACTIVE_FACE_STYLE
				: {};
		return {
			width: size,
			height: size,
			["--sq-octagon-outer-clip"]: clipPaths.outerClipPath,
			["--sq-octagon-inner-clip"]: clipPaths.innerClipPath,
			["--sq-octagon-stroke-clip"]: clipPaths.strokeClipPath,
			["--sq-octagon-face-clip"]: clipPaths.faceClipPath,
			["--sq-octagon-stroke-scale"]: `${clipPaths.strokeScale}`,
			["--sq-octagon-face-scale"]: `${clipPaths.faceScale}`,
			...layerStyle,
			...activeFaceStyle,
		} as CSSProperties;
	}, [active, clipPaths, layerOverrides, size, surface]);

	const badgeTile = (
		<div
			className="relative shrink-0 overflow-visible"
			style={
				{
					width: size,
					height: size,
					["--sq-octagon-outer-clip"]: clipPaths.outerClipPath,
					["--sq-octagon-inner-clip"]: clipPaths.innerClipPath,
					["--sq-octagon-face-clip"]: clipPaths.faceClipPath,
				} as CSSProperties
			}
		>
			{outerGlassClipPaths ? (
				<div
					className="sq-octagon-badge-outer-glass pointer-events-none absolute top-1/2 left-1/2 z-0 -translate-x-1/2 -translate-y-1/2 overflow-visible [clip-path:var(--sq-octagon-outer-glass-clip)]"
					data-shell-tone={shellTone}
					style={
						{
							width: resolveSkillBadgeOuterGlassSizePx(size),
							height: resolveSkillBadgeOuterGlassSizePx(size),
							["--sq-octagon-outer-glass-clip"]:
								outerGlassClipPaths.outerClipPath,
							["--sq-octagon-inner-clip"]:
								outerGlassClipPaths.innerClipPath,
						} as CSSProperties
					}
				>
					<div
						className={cn(
							"sq-octagon-badge-outer-glass-shell pointer-events-none absolute inset-0",
							OCTAGON_SHELL_CLIP_CLASS,
						)}
					/>
				</div>
			) : null}
			<div
				className={cn(
					"sq-octagon-badge sq-octagon-button relative z-[1] overflow-visible [clip-path:var(--sq-octagon-outer-clip)]",
					isDisabled &&
						surface === "button" &&
						"cursor-not-allowed opacity-40",
				)}
				style={tileStyle}
				data-surface={surface}
				data-interactive={interactive ? "true" : "false"}
				data-active={active ? "true" : "false"}
				data-disabled={isDisabled ? "true" : "false"}
				data-hover-variant={hoverVariant}
				data-shell-tone={surface === "skill" ? shellTone : undefined}
				data-skill-variant={
					surface === "skill" ? skillVariant : undefined
				}
				aria-hidden={ariaHidden ? "true" : undefined}
			>
				<div
					className={cn(
						"sq-octagon-badge-shell sq-octagon-button-shell pointer-events-none absolute inset-0",
						OCTAGON_SHELL_CLIP_CLASS,
					)}
				/>
				<div
					className={cn(
						"sq-octagon-badge-stroke sq-octagon-button-stroke pointer-events-none absolute inset-0 scale-[var(--sq-octagon-stroke-scale)]",
						OCTAGON_STROKE_CLIP_CLASS,
					)}
				/>
				<div
					className={cn(
						"sq-octagon-badge-face sq-octagon-button-face pointer-events-none absolute inset-0 origin-center scale-[var(--sq-octagon-face-scale)]",
						OCTAGON_FACE_CLIP_CLASS,
					)}
				/>
				{innerOverlay ? (
					<div
						className={cn(
							"sq-skill-badge-shine pointer-events-none absolute inset-0 z-[3] overflow-hidden",
							OCTAGON_SHELL_CLIP_CLASS,
						)}
					>
						{innerOverlay}
					</div>
				) : null}
			</div>
			{children != null ? (
				<div className="sq-octagon-badge-icon pointer-events-none absolute inset-0 z-[5] grid place-items-center overflow-visible">
					{children}
				</div>
			) : null}
		</div>
	);

	const shadowHost = (
		<div
			className={cn(
				"sq-octagon-badge-shadow-host relative flex shrink-0 items-center justify-center overflow-visible",
				shadowHostClassName,
				layoutFootprint === "shadow-host" &&
					tooltip == null &&
					className,
			)}
			style={{ width: hostSize, height: hostSize }}
			data-drop-shadow={dropShadowPreset || "false"}
		>
			{badgeTile}
		</div>
	);

	const centeredShadowHost = (
		<div
			className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
			style={{ width: hostSize, height: hostSize }}
		>
			{shadowHost}
		</div>
	);

	const badgeBody =
		layoutFootprint === "visual" ? (
			<div
				className={cn(
					"relative shrink-0 overflow-visible",
					tooltip == null && className,
				)}
				style={{ width: footprintSize, height: footprintSize }}
				role="img"
				aria-label={tooltip == null ? ariaLabel : undefined}
			>
				{centeredShadowHost}
			</div>
		) : (
			shadowHost
		);

	if (tooltip == null) {
		return badgeBody;
	}

	return (
				<ContentTooltip
			{...CONTENT_TOOLTIP_SHORT}
			side={tooltipSide}
			sideOffset={tooltipSideOffset}
			content={tooltip}
		>
			<span
								className={cn(
									"relative inline-flex shrink-0 cursor-default items-center justify-center overflow-visible",
									className,
								)}
								style={{ width: footprintSize, height: footprintSize }}
								role="img"
								aria-label={ariaLabel}
							>
								{layoutFootprint === "visual"
									? centeredShadowHost
									: shadowHost}
							</span>
		</ContentTooltip>
	);
}
